import { useEffect, useRef, useState } from "react";
import { getGalleryCollectedPlayers, setCollectedFirstOwner, } from "../api/gallery/galleryCollectedPlayers";
import { includesIgnoringAccents } from "../utils/sbcListControls";
// The backend's @ArrayMaxSize on PUT /user-players/collected/first-owner.
const MAX_IDS_PER_SAVE = 2000;
/**
 * The First Owner sheet's data: the user's collected cards for one set, read
 * fresh on mount (hosts mount it per open), with optimistic flag toggles. `close` reports a change
 * only once every save in flight has landed.
 */
export const useGalleryFirstOwners = ({ setId, onClose, onChanged, onSaveFailed, hiddenIds, }) => {
    const [players, setPlayers] = useState();
    const [search, setSearch] = useState("");
    const [error, setError] = useState(false);
    // A sync or another set's sheet may have changed them.
    useEffect(() => {
        let cancelled = false;
        setPlayers(undefined);
        setError(false);
        getGalleryCollectedPlayers(setId)
            .then((result) => !cancelled && setPlayers(result))
            .catch(() => !cancelled && setError(true));
        return () => {
            cancelled = true;
        };
    }, [setId]);
    const saves = useRef(Promise.resolve());
    const changed = useRef(false);
    const close = () => {
        onClose();
        void saves.current.then(() => {
            if (!changed.current)
                return;
            changed.current = false;
            onChanged?.();
        });
    };
    // Saved in batches the backend accepts; a failed batch puts its old flags back.
    const update = (resourceIds, firstOwner) => {
        const apply = (ids, value) => setPlayers((prev) => prev?.map((p) => ids.has(p.resourceId) ? { ...p, firstOwner: value(p) } : p));
        const previous = new Map(players?.map((p) => [p.resourceId, p.firstOwner]));
        apply(new Set(resourceIds), () => firstOwner);
        let failed = false;
        const batches = [];
        for (let i = 0; i < resourceIds.length; i += MAX_IDS_PER_SAVE) {
            const batch = resourceIds.slice(i, i + MAX_IDS_PER_SAVE);
            batches.push(setCollectedFirstOwner(batch, firstOwner).then(() => {
                changed.current = true;
            }, () => {
                failed = true;
                apply(new Set(batch), (p) => !!previous.get(p.resourceId));
            }));
        }
        const save = Promise.all(batches).then(() => failed && onSaveFailed?.());
        saves.current = Promise.all([saves.current, save]);
    };
    const visible = players?.filter((p) => !hiddenIds?.includes(p.resourceId) &&
        includesIgnoringAccents(p.player.name, search));
    // Marks only what the search shows.
    const notFirstOwner = visible?.filter((p) => !p.firstOwner) ?? [];
    return {
        players,
        visible,
        error,
        search,
        setSearch,
        close,
        toggle: (p) => update([p.resourceId], !p.firstOwner),
        canMarkAll: notFirstOwner.length > 0,
        markAll: () => update(notFirstOwner.map((p) => p.resourceId), true),
    };
};
//# sourceMappingURL=useGalleryFirstOwners.js.map